import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { 
  MessageSquare, Mail, Phone, User, Clock, Check, CheckCheck, 
  Trash2, RefreshCw, Inbox, UserX, ShieldCheck
} from 'lucide-react';

const SMSInboxAdmin = () => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, stop_requests: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread
  const [busyContactId, setBusyContactId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const unreadOnly = filter === 'unread' ? '?unread_only=true' : '';
      const response = await fetch(`${API_URL}/api/sms-webhook/inbox${unreadOnly}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/sms-webhook/inbox/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/sms-webhook/inbox/${messageId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchMessages();
      fetchStats();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/sms-webhook/inbox/mark-all-read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchMessages();
      fetchStats();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/sms-webhook/inbox/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchMessages();
      fetchStats();
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const unsubscribeContact = async (contactId, contactName) => {
    if (!contactId) {
      alert('No contact linked to this message');
      return;
    }
    if (!window.confirm(`Unsubscribe ${contactName || 'this contact'} from SMS? This sets a permanent opt-out (TCPA) so they cannot be re-subscribed via forms or CSV imports.`)) {
      return;
    }
    try {
      setBusyContactId(contactId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contacts/${contactId}/opt-out-sms`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert(`${contactName || 'Contact'} has been unsubscribed from SMS`);
        await fetchMessages();
        await fetchStats();
      } else {
        alert('Failed to unsubscribe contact');
      }
    } catch (err) {
      console.error('Error unsubscribing contact:', err);
      alert('Failed to unsubscribe contact');
    } finally {
      setBusyContactId(null);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Unknown';
    const clean = phone.replace(/\D/g, '').slice(-10);
    if (clean.length === 10) {
      return `(${clean.slice(0,3)}) ${clean.slice(3,6)}-${clean.slice(6)}`;
    }
    return phone;
  };

  const isStopRequest = (message) => {
    const lower = (message || '').toLowerCase().trim();
    return ['stop', 'unsubscribe', 'cancel', 'end', 'quit'].includes(lower);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-pink-500" />
        <span className="ml-2">Loading messages...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black flex items-center gap-2">
            <Inbox className="w-7 h-7 text-pink-600" />
            SMS Inbox
          </h1>
          <p className="text-gray-600">Incoming SMS replies from customers</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => { fetchMessages(); fetchStats(); }}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          {stats.unread > 0 && (
            <Button onClick={markAllRead} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <CheckCheck className="w-4 h-4" />
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-pink-600">{stats.total}</p>
          <p className="text-sm text-gray-600">Total Messages</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-blue-600">{stats.unread}</p>
          <p className="text-sm text-gray-600">Unread</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-red-600">{stats.stop_requests}</p>
          <p className="text-sm text-gray-600">Unsubscribe Requests</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all' 
              ? 'bg-pink-500 text-white' 
              : 'bg-gray-100 hover:bg-gray-200'
          }`}
        >
          All Messages
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'unread' 
              ? 'bg-pink-500 text-white' 
              : 'bg-gray-100 hover:bg-gray-200'
          }`}
        >
          Unread Only {stats.unread > 0 && `(${stats.unread})`}
        </button>
      </div>

      {/* Messages List */}
      <div className="bg-white rounded-xl shadow-lg">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No messages yet</p>
            <p className="text-sm">Incoming SMS replies will appear here</p>
          </div>
        ) : (
          <div className="divide-y">
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  !msg.is_read ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-bold flex items-center gap-1">
                        <Phone className="w-4 h-4 text-gray-500" />
                        {formatPhone(msg.from_number)}
                      </span>
                      
                      {msg.contact_name && (
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {msg.contact_name}
                        </span>
                      )}
                      
                      {!msg.is_read && (
                        <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-medium">
                          NEW
                        </span>
                      )}
                      
                      {isStopRequest(msg.message) && (
                        msg.auto_unsubscribed ? (
                          <span
                            className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium"
                            data-testid="unsubscribe-pill-done"
                          >
                            UNSUBSCRIBE
                          </span>
                        ) : msg.contact_id ? (
                          <button
                            onClick={() => unsubscribeContact(msg.contact_id, msg.contact_name)}
                            disabled={busyContactId === msg.contact_id}
                            className="px-2 py-0.5 bg-red-500 hover:bg-red-600 text-white text-xs rounded-full font-medium inline-flex items-center gap-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Click to unsubscribe this contact from SMS (permanent TCPA opt-out)"
                            data-testid="unsubscribe-pill-button"
                          >
                            <UserX className="w-3 h-3" />
                            {busyContactId === msg.contact_id ? 'UNSUBSCRIBING…' : 'UNSUBSCRIBE'}
                          </button>
                        ) : (
                          <span
                            className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium"
                            title="STOP received from a number not linked to any contact"
                            data-testid="unsubscribe-pill-unlinked"
                          >
                            UNSUBSCRIBE
                          </span>
                        )
                      )}

                      {isStopRequest(msg.message) && msg.auto_unsubscribed && (
                        <span
                          className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full font-medium inline-flex items-center gap-1"
                          title="Contact was automatically unsubscribed from future SMS by the webhook handler."
                          data-testid="auto-unsubscribed-badge"
                        >
                          <ShieldCheck className="w-3 h-3" />
                          Auto-unsubscribed
                        </span>
                      )}
                    </div>
                    
                    {/* Message Content */}
                    <div className={`p-3 rounded-lg ${
                      isStopRequest(msg.message) 
                        ? 'bg-red-50 border border-red-200' 
                        : 'bg-gray-100'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                    </div>
                    
                    {/* Footer */}
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(msg.received_at).toLocaleString()}
                      </span>
                      {msg.contact_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {msg.contact_email}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {!msg.is_read && (
                      <button
                        onClick={() => markAsRead(msg.id)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteMessage(msg.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSInboxAdmin;
